import { FlexGrid, FlexGridDirection } from '@ctek/design-system';
import { AssignmentStatus } from 'century-core/core-apis/assignment/types';
import { getFeatureFlagData } from 'century-core/core-auth/utils';
import { useAuth } from 'century-core/core-auth/hooks/useAuth';
import { useTranslateSubject } from 'century-core/core-components/Subject/SubjectLabel';
import {
  FormikDatePicker,
  FormikTimePicker,
  FormikTextInput,
  FormikTextArea,
  FormikSelect,
  LabelWithError,
} from 'century-core/core-utils/forms/formik-fields';
import { YES_NO_OPTIONS } from 'century-core/core-utils/forms/options';
import { extractDateAndTime } from 'century-core/core-utils/utils/date/date';
import { Field, useFormikContext } from 'formik';
import React from 'react';
import { AssignmentFormData, getCurrentHour, getDateRanges, GroupedDropdown, LATE_SUBMISSION_OPTIONS } from '../helpers/helpers';
import { useAssignmentLabelsTranslation } from '../helpers/hooks';
import { getDisabledStatus } from '../helpers/validateAssignment';

export const Settings = ({ subjects, assignmentStatus }: { subjects: GroupedDropdown[]; assignmentStatus?: AssignmentStatus }) => {
  const translation = useAssignmentLabelsTranslation();
  const auth = useAuth();
  // This extends the due date to 1 year
  const hasExtendedDueDate = getFeatureFlagData<{ extendedDueDate: boolean }>(auth, 'assignments', 'newAssignments')?.extendedDueDate;
  const { values, setFieldValue } = useFormikContext<AssignmentFormData>();
  const { startDateRange, dueDateRange } = getDateRanges(values.startDate, hasExtendedDueDate);
  const translateSubject = useTranslateSubject();

  React.useEffect(() => {
    if (!values.startDate || getDisabledStatus(assignmentStatus, 'startDate')) {
      return;
    }

    const { date: todayDate, time: currentTime } = extractDateAndTime(new Date().toISOString());
    if (values.startDate === todayDate && values.startTime < currentTime) {
      setFieldValue('startTime', getCurrentHour(new Date(), 15));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.startDate]);

  return (
    <FlexGrid direction={FlexGridDirection.COLUMN}>
      <FlexGrid.Col fullHeightContent={false}>
        <LabelWithError name="assignmentName" label={translation.assignmentName}>
          <FormikTextInput name="assignmentName" maxLength={200} />
        </LabelWithError>
      </FlexGrid.Col>
      <FlexGrid.Col fullHeightContent={false}>
        <LabelWithError name="instructions" label={translation.instructions}>
          <FormikTextArea name="instructions" maxLength={1000} disabled={getDisabledStatus(assignmentStatus)} />
        </LabelWithError>
      </FlexGrid.Col>
      <FlexGrid.Col fullHeightContent={false}>
        <LabelWithError name="subject" label={translation.subject}>
          <Field as="select" name="subject" id="select" disabled={getDisabledStatus(assignmentStatus, 'subject')} className="cds-select">
            {subjects.map(group => (
              <optgroup key={group.key} label={group.title}>
                {group.entities.map(subject => (
                  <option key={subject.value} value={subject.value}>
                    {translateSubject(subject.title)}
                  </option>
                ))}
              </optgroup>
            ))}
          </Field>
        </LabelWithError>
      </FlexGrid.Col>

      <FlexGrid.Col fullHeightContent={false}>
        <FlexGrid direction={FlexGridDirection.ROW}>
          <FlexGrid.Col sm={6}>
            <LabelWithError name="lateSubmission" label={translation.lateSubmission}>
              <FormikSelect
                name="lateSubmission"
                options={LATE_SUBMISSION_OPTIONS}
                disabled={getDisabledStatus(assignmentStatus, 'lateSubmission')}
              />
            </LabelWithError>
          </FlexGrid.Col>
          <FlexGrid.Col sm={6}>
            <LabelWithError name="multipleAttempts" label={translation.multipleAttempts}>
              <FormikSelect
                name="multipleAttempts"
                options={YES_NO_OPTIONS.slice(1).reverse()}
                disabled={getDisabledStatus(assignmentStatus, 'multipleAttempts')}
              />
            </LabelWithError>
          </FlexGrid.Col>
        </FlexGrid>
      </FlexGrid.Col>
      <FlexGrid.Col fullHeightContent={false}>
        <FlexGrid direction={FlexGridDirection.ROW}>
          <FlexGrid.Col sm={6} fullHeightContent={false}>
            <LabelWithError name="startDate" label={translation.startDate}>
              <FormikDatePicker
                name="startDate"
                min={startDateRange[0]}
                max={startDateRange[1]}
                disabled={getDisabledStatus(assignmentStatus, 'startDate')}
              />
            </LabelWithError>
          </FlexGrid.Col>
          <FlexGrid.Col sm={6} fullHeightContent={false}>
            <LabelWithError name="startTime" label={translation.startTime}>
              <FormikTimePicker name="startTime" disabled={getDisabledStatus(assignmentStatus, 'startTime') || values.startDate === ''} />
            </LabelWithError>
          </FlexGrid.Col>
        </FlexGrid>
      </FlexGrid.Col>
      <FlexGrid.Col fullHeightContent={false}>
        <FlexGrid direction={FlexGridDirection.ROW}>
          <FlexGrid.Col sm={6} fullHeightContent={false}>
            <LabelWithError name="dueDate" label={translation.dueDate}>
              <FormikDatePicker
                name="dueDate"
                min={dueDateRange[0]}
                max={dueDateRange[1]}
                disabled={getDisabledStatus(assignmentStatus, 'dueDate')}
              />
            </LabelWithError>
          </FlexGrid.Col>
          <FlexGrid.Col sm={6} fullHeightContent={false}>
            <LabelWithError name="dueTime" label={translation.dueTime}>
              <FormikTimePicker name="dueTime" disabled={getDisabledStatus(assignmentStatus, 'dueTime') || values.dueDate === ''} />
            </LabelWithError>
          </FlexGrid.Col>
        </FlexGrid>
      </FlexGrid.Col>
    </FlexGrid>
  );
};
